import { useEffect, useState } from "react";
import { api } from "@/lib/api";

/** Compact reference picker — shows in AI modals. By default NO references are
 *  selected; the user opts in by clicking chips.
 *
 *  Props:
 *    value: string[] selected reference ids
 *    onChange: (ids: string[]) => void
 *    suggestedType: string optional, to pre-filter by project type
 */
export default function ReferencePicker({ value, onChange, suggestedType }) {
  const [refs, setRefs] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [filter, setFilter] = useState("");   // search

  useEffect(() => {
    api.get("/references").then((r) => { setRefs(r.data || []); setLoaded(true); });
  }, []);

  const visible = refs.filter((r) =>
    !filter || (r.title || "").toLowerCase().includes(filter.toLowerCase())
            || (r.director || "").toLowerCase().includes(filter.toLowerCase())
  );

  const toggle = (id) => {
    if (value.includes(id)) onChange(value.filter((x) => x !== id));
    else onChange([...value, id]);
  };

  if (loaded && refs.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-[#2C2B29]/15 bg-[#FAFAF6] p-3 text-[12px] text-[#2C2B29]/60"
           data-testid="ref-picker-empty">
        <strong>Biblioteca vacía.</strong> Sube dossieres en <a href="/library" target="_blank" rel="noreferrer" className="text-[#26775E] underline">Biblioteca</a> para usarlos como referencia de voz.
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-[#2C2B29]/10 bg-white" data-testid="ref-picker">
      <button type="button" onClick={() => setExpanded(!expanded)}
              data-testid="ref-picker-toggle"
              className="flex w-full items-center justify-between px-3 py-2 text-left">
        <span className="text-[11px] font-semibold uppercase tracking-[0.2em] text-[#2C2B29]/70">
          Referencias de voz
          {value.length > 0 && (
            <span className="ml-2 rounded-full bg-[#26775E] px-2 py-0.5 text-[10px] text-white">{value.length}</span>
          )}
        </span>
        <span className={`text-[#2C2B29]/50 transition-transform ${expanded ? "rotate-180" : ""}`}>▾</span>
      </button>
      {expanded && (
        <div className="border-t border-[#2C2B29]/10 p-3" data-testid="ref-picker-panel">
          <input value={filter} onChange={(e) => setFilter(e.target.value)}
                 data-testid="ref-picker-filter"
                 placeholder="Buscar por título o director…"
                 className="mb-3 w-full rounded border border-[#2C2B29]/15 bg-white px-2.5 py-1.5 text-[12px] outline-none focus:border-[#26775E]" />
          <div className="flex max-h-48 flex-col gap-1 overflow-y-auto">
            {visible.map((r) => {
              const on = value.includes(r.id);
              return (
                <button key={r.id} type="button" onClick={() => toggle(r.id)}
                        data-testid={`ref-chip-${r.id}`}
                        className={`flex items-center gap-2 rounded-md border px-2.5 py-1.5 text-left text-[12px] transition ${
                          on
                            ? "border-[#26775E] bg-[#26775E]/10"
                            : "border-[#2C2B29]/10 bg-white hover:border-[#2C2B29]/30"
                        }`}>
                  <span className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border ${on ? "border-[#26775E] bg-[#26775E] text-white" : "border-[#2C2B29]/25"}`}>
                    {on ? <svg viewBox="0 0 20 20" className="h-3 w-3 fill-white"><path d="M16.7 5.3l-8.4 8.4L3.3 8.7l1.4-1.4 3.6 3.6 7-7z" /></svg> : null}
                  </span>
                  <span className="flex-1">
                    <span className="font-medium text-[#2C2B29]">{r.title}</span>
                    <span className="ml-2 text-[10px] text-[#2C2B29]/50">
                      {r.project_type}{r.director ? ` · ${r.director}` : ""}{r.year ? ` · ${r.year}` : ""}
                    </span>
                  </span>
                </button>
              );
            })}
            {visible.length === 0 && (
              <p className="px-2 py-3 text-center text-[12px] text-[#2C2B29]/50">
                Sin resultados para "{filter}".
              </p>
            )}
          </div>
          <p className="mt-3 text-[10px] leading-relaxed text-[#2C2B29]/55">
            Claude usará las referencias marcadas como <strong>inspiración de tono y voz</strong>, no copiará su contenido.
          </p>
        </div>
      )}
    </div>
  );
}
